import { Alert } from "@mui/material";
import * as React from "react";
import { useParams, useSearchParams } from "react-router";

import { useAuth } from "@/providers";
import { PageLayout } from "@/shared/components/PageLayout";
import { ResourceView } from "@/shared/components/ResourceView";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import type { S3ObjectEntry } from "@/shared/domain/integrations";
import { useS3BucketEntries } from "@/shared/domain/integrations/hooks/useS3BucketEntries";
import { DownloadForOfflineOutlinedIcon } from "@/shared/foundation/icons/mui";
import { Spinner } from "@/shared/foundation/primitives";
import {
  deriveLoadingState,
  useTable,
} from "@/shared/foundation/primitives/Table";
import { RobotoDomainException, RobotoNotFoundException } from "@/shared/http";
import { pluralize } from "@/shared/text";

import { ExplorerBreadcrumbs } from "./ExplorerBreadcrumbs";
import { ImportObjectsDialog } from "./ImportObjectsDialog";
import { LevelTable } from "./LevelTable";
import styles from "./S3BucketExplorer.module.css";
import { WrongOrgState } from "./WrongOrgState";

const HTTP_NOT_FOUND = 404;

interface ExplorerContentProps {
  bucketName: string;
  prefix: string;
  orgId: string;
}

function ExplorerContent({ bucketName, prefix, orgId }: ExplorerContentProps) {
  const table = useTable<S3ObjectEntry>({ getRowId: (row) => row.key });

  // Selection lives here, above both the table and the folder the operator is standing in, so
  // it accumulates across folders. `DataTable`'s own selection is cleared by `resetCurrentPage`,
  // which this page calls on every navigation.
  const [selected, setSelected] = React.useState<
    ReadonlyMap<string, S3ObjectEntry>
  >(new Map());
  const [isImporting, setIsImporting] = React.useState(false);

  const toggleEntry = React.useCallback(
    (entry: S3ObjectEntry, isSelected: boolean) => {
      setSelected((current) => {
        const next = new Map(current);
        if (isSelected) {
          next.set(entry.key, entry);
        } else {
          next.delete(entry.key);
        }
        return next;
      });
    },
    [],
  );

  const togglePage = React.useCallback(
    (entries: S3ObjectEntry[], isSelected: boolean) => {
      setSelected((current) => {
        const next = new Map(current);
        for (const entry of entries) {
          if (isSelected) {
            next.set(entry.key, entry);
          } else {
            next.delete(entry.key);
          }
        }
        return next;
      });
    },
    [],
  );

  const clearSelection = React.useCallback(() => setSelected(new Map()), []);

  const { resetCurrentPage } = table;
  React.useEffect(
    function startAtTheTopOfANewFolder() {
      resetCurrentPage();
    },
    [prefix, resetCurrentPage],
  );

  const level = useS3BucketEntries(
    { bucketName, orgId, prefix },
    table.currentPage,
    table.pageSize,
  );

  if (isBucketUnknownToOrg(level.error)) {
    return <WrongOrgState bucketName={bucketName} />;
  }

  if (level.error !== null) {
    return (
      <Alert severity="error" className={styles.failure}>
        Could not list this bucket: {describeFailure(level.error)}
      </Alert>
    );
  }

  const rows = level.data ?? [];
  const selectedEntries = Array.from(selected.values());

  return (
    <>
      <div className={styles.actions}>
        <ResourceView.Selection<S3ObjectEntry>
          selection={selectedEntries}
          clearSelection={clearSelection}
          options={[
            {
              name: `Import ${selectedEntries.length} ${pluralize("file", selectedEntries.length)}`,
              icon: <DownloadForOfflineOutlinedIcon fontSize="small" />,
              action: () => setIsImporting(true),
            },
          ]}
        />
      </div>

      <div className={styles.tableFrame}>
        <LevelTable
          table={table}
          bucketName={bucketName}
          prefix={prefix}
          rows={rows}
          loading={deriveLoadingState({
            isLoading: level.isLoading,
            isFetching: level.isFetching,
          })}
          hasNextPage={level.hasNextPage}
          selectedKeys={new Set(selected.keys())}
          onToggle={toggleEntry}
          onTogglePage={togglePage}
        />
      </div>

      <ImportObjectsDialog
        bucketName={bucketName}
        orgId={orgId}
        entries={isImporting ? selectedEntries : []}
        onClose={() => setIsImporting(false)}
        onImported={clearSelection}
      />
    </>
  );
}

/**
 * Is this the failure a bucket the active org has not registered produces?
 *
 * A 404 whose body parsed as a Roboto error envelope reconstructs its own subclass. A bodyless
 * or unparseable one cannot: four exception types claim 404, so the client declines to guess and
 * hands back the base class, which still carries the status.
 */
function isBucketUnknownToOrg(error: Error | null): boolean {
  if (error instanceof RobotoNotFoundException) {
    return true;
  }
  if (!(error instanceof RobotoDomainException)) {
    return false;
  }
  const { httpStatusCode } = error.constructor as typeof RobotoDomainException;
  return httpStatusCode === HTTP_NOT_FOUND;
}

function describeFailure(error: Error): string {
  return error.message.length > 0 ? error.message : "the request was refused";
}

/**
 * Browse a customer's bring-your-own bucket and import objects from it.
 *
 * Roboto staff only, and the gate wraps the content rather than the route so none of the admin
 * queries mount for anyone else. Every listing goes through Roboto's API using the cross-account
 * role the customer granted, so no AWS credential for their bucket ever reaches the browser.
 */
export function S3BucketExplorer() {
  const { bucketName = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { currentOrganization, initialAuthCheckComplete } = useAuth();

  const prefix = searchParams.get("prefix") ?? "";

  return (
    <PageLayout
      title={`Roboto - S3 - ${bucketName}`}
      headerContentLeft={
        <ExplorerBreadcrumbs bucketName={bucketName} prefix={prefix} />
      }
    >
      {/*
        Until the session resolves, the viewer is neither staff nor not-staff and has no org, so
        rendering either verdict would flash an accusation or an instruction at an operator who
        needs neither.
      */}
      {!initialAuthCheckComplete ? (
        <Spinner />
      ) : (
        <SystemUserOnly
          fallback={
            <Alert severity="warning">
              The bucket explorer is available to Roboto staff only.
            </Alert>
          }
        >
          {currentOrganization === null ? (
            <Alert severity="info">
              Choose the organization whose registration authorizes this bucket.
            </Alert>
          ) : (
            <ExplorerContent
              bucketName={bucketName}
              prefix={prefix}
              orgId={currentOrganization.org_id}
            />
          )}
        </SystemUserOnly>
      )}
    </PageLayout>
  );
}
